// Shared helpers for rendering achievements consistently across the
// CelebrationModal, the Dashboard tile, and the /achievements gallery.

import type { AchievementKind } from '@busy/api';

// Per-category catalog: locked tile order + display formatting. The
// thresholds + tier grouping MUST stay in sync with check_achievements()
// in the latest tier migration — that function is the source of truth
// for what actually gets awarded.
//
// CURRENT SOURCES (kept in sync as of last audit):
//   - 0053_achievement_hardening.sql — revenue retier + anti-gaming caps
//   - 0060_duration_floor_6min.sql   — raised eligibility floor 5→6 min
//                                       and switched revenue to
//                                       original_price_cents ratcheting
//   - 0064_achievement_progress_rpc.sql — the get_user_achievement_progress
//                                       function exposes the live counts
//                                       that match what check_achievements
//                                       awards on.
//
// History: two drifts caught + fixed previously. (1) A "currently 0022"
// header note went stale through 0049/0053. (2) The revenue thresholds
// array on the client stayed on the old [$100 → $100K] ladder for a
// while after 0053 shipped the new [$500 → $150K] one, which made T1
// ($100) appear as a goal but the DB never awarded it. Symptoms: a
// permanently-locked "$100 revenue" tile + an invisible $150K top tier.
//
// When you next touch SQL achievement thresholds, mirror the change here
// AND bump the source list above to reference the new migration.
//
// Tiers: each kind has 4 tiers × 2 thresholds = 8 milestones. The tier
// grouping is rendered as sub-sections in the gallery so progression feels
// staged rather than a single 8-tile blob.
export const ACHIEVEMENT_CATALOG: Record<AchievementKind, {
  /** Flat threshold list — order = display order, tiered as 4 pairs of 2. */
  thresholds: number[];
  /** Same thresholds split into [T1, T2, T3, T4]. */
  tiers: [number[], number[], number[], number[]];
  /** Tailwind classes for the unlocked tile gradient. */
  gradient: string;
  /** Tailwind text-color class for the icon when unlocked. */
  iconColor: string;
}> = {
  streak: {
    thresholds: [1, 7, 30, 60, 90, 180, 365, 730],
    tiers: [[1, 7], [30, 60], [90, 180], [365, 730]],
    gradient: 'bg-gradient-to-br from-red-600 to-orange-500',
    iconColor: 'text-orange-100',
  },
  clients: {
    thresholds: [1, 10, 25, 50, 75, 100, 150, 250],
    tiers: [[1, 10], [25, 50], [75, 100], [150, 250]],
    gradient: 'bg-gradient-to-br from-navy-800 to-navy-900',
    iconColor: 'text-gold-400',
  },
  cuts: {
    thresholds: [1, 25, 50, 100, 250, 500, 1000, 2500],
    tiers: [[1, 25], [50, 100], [250, 500], [1000, 2500]],
    gradient: 'bg-gradient-to-br from-gold-500 to-gold-400',
    iconColor: 'text-navy-900',
  },
  revenue: {
    // Cents. Matches the SQL ladder in 0053:
    //   T1=$500, T2=$1K, T3=$5K, T4=$10K, T5=$25K, T6=$50K, T7=$100K, T8=$150K
    // Previously this array started at $100 (10000 cents) and topped
    // at $100K — both wrong vs 0053. The $100 tier was never awarded
    // server-side, and the $150K top tier was missing entirely.
    thresholds: [50000, 100000, 500000, 1000000, 2500000, 5000000, 10000000, 15000000],
    tiers: [
      [50000, 100000],        // T1=$500, T2=$1K
      [500000, 1000000],      // T3=$5K, T4=$10K
      [2500000, 5000000],     // T5=$25K, T6=$50K
      [10000000, 15000000],   // T7=$100K, T8=$150K
    ],
    gradient: 'bg-gradient-to-br from-green-600 to-green-700',
    iconColor: 'text-green-100',
  },
  lessons: {
    thresholds: [1, 10, 25, 50, 75, 100, 250, 500],
    tiers: [[1, 10], [25, 50], [75, 100], [250, 500]],
    gradient: 'bg-gradient-to-br from-navy-700 to-red-700',
    iconColor: 'text-gold-400',
  },
  transformations: {
    thresholds: [1, 5, 10, 25, 50, 75, 100, 250],
    tiers: [[1, 5], [10, 25], [50, 75], [100, 250]],
    gradient: 'bg-gradient-to-br from-red-600 to-gold-500',
    iconColor: 'text-white',
  },
  // Timekeeper — cumulative hours of timed cuts. Replaces the gameable
  // 'capacity' kind. Top tier 2500h ≈ 1.4 years for a full-time barber.
  timekeeper: {
    thresholds: [1, 10, 50, 100, 250, 500, 1000, 2500],
    tiers: [[1, 10], [50, 100], [250, 500], [1000, 2500]],
    gradient: 'bg-gradient-to-br from-emerald-600 to-navy-800',
    iconColor: 'text-gold-300',
  },
};

/**
 * Returns the i18n key + vars for the threshold display string. Caller resolves
 * via `t(key, vars)`.
 *
 * Examples:
 *   thresholdLabel('streak', 7)      → { key: 'ach.thr.streak_other', vars: { n: 7 } }
 *   thresholdLabel('revenue', 1000000) → { key: 'ach.thr.revenue', vars: { amount: '10,000' } }
 */
export function thresholdLabel(
  kind: AchievementKind,
  threshold: number,
): { key: string; vars: Record<string, string | number> } {
  if (kind === 'revenue') {
    return {
      key: 'ach.thr.revenue',
      vars: { amount: formatRevenue(threshold) },
    };
  }
  const isOne = threshold === 1;
  return {
    key: isOne ? `ach.thr.${kind}_one` : `ach.thr.${kind}_other`,
    vars: { n: formatThreshold(threshold) },
  };
}

/** "1,000" → "1K", "100,000" → "100K", "1,000,000" → "1M". */
function formatThreshold(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1) + 'K';
  return String(n);
}

/** Revenue thresholds are in cents. Display as "$1K", "$1M" etc. */
function formatRevenue(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return (dollars / 1_000_000).toFixed(dollars % 1_000_000 === 0 ? 0 : 1) + 'M';
  if (dollars >= 1_000)     return (dollars / 1_000).toFixed(dollars % 1_000 === 0 ? 0 : 1) + 'K';
  return String(dollars);
}

/** Sort earned achievements newest-first. */
export function byEarnedDesc<T extends { earned_at: string }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => new Date(b.earned_at).getTime() - new Date(a.earned_at).getTime(),
  );
}

// `nextThreshold` lives in NextMilestonesTile.tsx now — that's the only
// surface that needs to compute "next milestone the user is working toward."
// The earlier stub here had a related-but-different bug (returned the first
// unearned threshold without checking current value); kept only as a comment
// in case future callers think they need it. They don't — go look at
// NextMilestonesTile.tsx for the canonical implementation.

/**
 * Maps a threshold to its 1-8 intensity level for `BadgeEffect`. Each kind
 * ships exactly 8 thresholds, so the ramp lines up cleanly with the canvas
 * engine's intensity scale: threshold[0] → 1, threshold[7] → 8. Higher
 * milestones produce a more dramatic celebration.
 */
export function intensityForThreshold(kind: AchievementKind, threshold: number): number {
  const idx = ACHIEVEMENT_CATALOG[kind].thresholds.indexOf(threshold);
  return idx < 0 ? 4 : idx + 1;
}

/**
 * For the gallery's locked-chip progress bars: returns the user's progress
 * 0..1 within the band (prevThreshold → threshold). The first threshold's
 * band starts at zero. Clamped, so a user past the threshold (waiting on
 * check_achievements to catch up) shows a full bar rather than overflowing.
 */
export function progressInBand(
  kind: AchievementKind,
  threshold: number,
  current: number,
): number {
  const all = ACHIEVEMENT_CATALOG[kind].thresholds;
  const idx = all.indexOf(threshold);
  const prev = idx > 0 ? all[idx - 1] ?? 0 : 0;
  const span = threshold - prev;
  if (span <= 0) return 0;
  const within = (current - prev) / span;
  if (within <= 0) return 0;
  if (within >= 1) return 1;
  return within;
}
